import { X, Plus, Users, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  useCreateTeamWithTrial,
  type CreateTrialMemberInput,
} from '@/hooks/useTeams';

interface CreateTrialTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  ownerEmail: string;
}

interface MemberRow {
  email: string;
  tier: 'plus' | 'power' | 'pro' | 'pro2';
  role: 'admin' | 'member';
}

export default function CreateTrialTeamModal({
  isOpen,
  onClose,
  ownerEmail,
}: CreateTrialTeamModalProps) {
  const [teamName, setTeamName] = useState('');
  const [ownerTier, setOwnerTier] = useState<'plus' | 'power' | 'pro' | 'pro2'>(
    'plus'
  );
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>(
    'annual'
  );
  const [trialEnd, setTrialEnd] = useState('');
  const [members, setMembers] = useState<MemberRow[]>([]);
  const createTeam = useCreateTeamWithTrial();
  const toast = useToast();

  if (!isOpen) return null;

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const validMembers = members
    .map((m) => ({ ...m, email: m.email.trim().toLowerCase() }))
    .filter((m) => m.email && m.email !== ownerEmail.toLowerCase());

  const duplicateEmails = validMembers.filter(
    (m, i) => validMembers.findIndex((other) => other.email === m.email) !== i
  );

  const canSubmit =
    !!teamName.trim() &&
    !!trialEnd &&
    trialEnd > today &&
    duplicateEmails.length === 0;

  const handleClose = () => {
    setTeamName('');
    setTrialEnd('');
    setMembers([]);
    setOwnerTier('plus');
    setBillingInterval('annual');
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const submitMembers: CreateTrialMemberInput[] = validMembers.map((m) => ({
      email: m.email,
      tier: m.tier,
      role: m.role,
    }));
    try {
      const res = await createTeam.mutateAsync({
        owner_email: ownerEmail,
        team_name: teamName.trim(),
        owner_tier: ownerTier,
        billing_interval: billingInterval,
        trial_end: trialEnd,
        members: submitMembers,
      });
      const inviteCount = res.members_invited.length;
      toast.success(
        `Team ${res.team_name} created`,
        `${res.seat_count} seat${res.seat_count === 1 ? '' : 's'}, ${inviteCount} invite${inviteCount === 1 ? '' : 's'} sent`
      );
      handleClose();
    } catch (e) {
      toast.error(
        'Failed to create team',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center sm:p-4"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Create team with trial
              </CardTitle>
              <CardDescription>Owner: {ownerEmail}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium">
                Creates a new team
              </span>
              <Button variant="ghost" size="sm" onClick={handleClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Team name</label>
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Acme Inc"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium mb-1 block">
                Owner tier
              </label>
              <div className="flex gap-2">
                {(['plus', 'power', 'pro', 'pro2'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setOwnerTier(t)}
                    className={`flex-1 px-3 py-1.5 rounded-md border text-sm capitalize ${
                      ownerTier === t
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-muted'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                Billing interval
              </label>
              <div className="flex gap-2">
                {(['annual', 'monthly'] as const).map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setBillingInterval(i)}
                    className={`flex-1 px-3 py-1.5 rounded-md border text-sm capitalize ${
                      billingInterval === i
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-muted'
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              Trial end date
            </label>
            <input
              type="date"
              min={today}
              max={maxDateStr}
              value={trialEnd}
              onChange={(e) => setTrialEnd(e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Maximum 1 year. Trial cancels if no payment method is added.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Members</label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  setMembers([
                    ...members,
                    { email: '', tier: 'plus', role: 'member' },
                  ])
                }
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add member
              </Button>
            </div>

            {members.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No members yet. The team will start with just the owner.
              </p>
            ) : (
              <div className="space-y-2">
                {members.map((m, idx) => {
                  const isDup =
                    !!m.email &&
                    validMembers.filter(
                      (vm) => vm.email === m.email.trim().toLowerCase()
                    ).length > 1;
                  const isOwnerEmail =
                    m.email.trim().toLowerCase() === ownerEmail.toLowerCase();
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex gap-2 items-center">
                        <input
                          type="email"
                          value={m.email}
                          onChange={(e) => {
                            const next = [...members];
                            next[idx] = { ...next[idx], email: e.target.value };
                            setMembers(next);
                          }}
                          placeholder="member@company.com"
                          className="flex-1 px-3 py-1.5 border rounded-md bg-background text-sm"
                        />
                        <select
                          value={m.tier}
                          onChange={(e) => {
                            const next = [...members];
                            next[idx] = {
                              ...next[idx],
                              tier: e.target.value as
                                | 'plus'
                                | 'power'
                                | 'pro'
                                | 'pro2',
                            };
                            setMembers(next);
                          }}
                          className="px-2 py-1.5 border rounded-md bg-background text-sm"
                        >
                          <option value="plus">Plus</option>
                          <option value="power">Power</option>
                          <option value="pro">Pro</option>
                          <option value="pro2">Pro2</option>
                        </select>
                        <select
                          value={m.role}
                          onChange={(e) => {
                            const next = [...members];
                            next[idx] = {
                              ...next[idx],
                              role: e.target.value as 'admin' | 'member',
                            };
                            setMembers(next);
                          }}
                          className="px-2 py-1.5 border rounded-md bg-background text-sm"
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setMembers(members.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {isOwnerEmail && (
                        <p className="text-xs text-destructive pl-1">
                          Cannot invite the team owner.
                        </p>
                      )}
                      {isDup && !isOwnerEmail && (
                        <p className="text-xs text-destructive pl-1">
                          Duplicate email.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
            <p className="font-medium">On submit:</p>
            <ul className="space-y-0.5 text-muted-foreground">
              <li>
                • A new team {teamName ? `"${teamName}"` : ''} will be created
              </li>
              <li>
                • {ownerEmail} becomes owner (plan:{' '}
                {ownerTier === 'plus'
                  ? 'Plus Monthly'
                  : ownerTier === 'power'
                    ? 'Power Monthly'
                    : ownerTier === 'pro'
                      ? 'Pro Monthly'
                      : 'Pro2 Monthly'}
                )
              </li>
              {validMembers.length > 0 && (
                <li>
                  • {validMembers.length} invite email
                  {validMembers.length === 1 ? '' : 's'} will be sent
                </li>
              )}
              {trialEnd && (
                <li>
                  • Trialing Stripe subscription is started until {trialEnd}
                </li>
              )}
            </ul>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || createTeam.isPending}
              className="flex-1"
            >
              {createTeam.isPending
                ? 'Creating...'
                : 'Create team & start trial'}
            </Button>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={createTeam.isPending}
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
